import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=70";
const LOGO = "https://customer-assets-39nsmqrw.emergentagent.net/job_manpower-hub-77/artifacts/eqhgezrt_Incheon%20Kia%20People%20and%20Culture%20Logo%20%281%29.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [issuedToken, setIssuedToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        navigate("/");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
        setIssuedToken(data.reset_token);
        setResetToken(data.reset_token);
        setMode("reset");
        toast.success("Reset token generated");
      } else {
        await api.post("/auth/reset-password", { token: resetToken.trim(), new_password: newPassword });
        toast.success("Password reset. Sign in with your new password.");
        setMode("login");
        setPassword("");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#BF9B30] focus:border-transparent";

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="People collaborating as a team" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0E2240]/85" />
        <div className="relative z-10 flex flex-col justify-end h-full p-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#BF9B30] mb-3">People &amp; Culture · Empowering Our People</div>
          <h1 className="font-serif text-4xl text-white tracking-tight">
            Manpower Control Tower
          </h1>
          <p className="text-white/70 mt-3 max-w-md text-sm leading-relaxed">
            Sanction, requisition and hiring visibility across 37 outlets and 22 departments — Incheon Motors Pvt. Ltd.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#F5F6F8] px-4">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <img src={LOGO} alt="Incheon Kia — People & Culture, Empowering Our People" data-testid="login-pnc-logo" className="h-20 w-auto -ml-2 mix-blend-multiply" />
            <div className="font-serif text-2xl text-[#0E2240] tracking-tight mt-4">
              HIRE<span className="text-[#BF9B30]">360</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mt-1.5">
              Manpower Control Tower
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8">
            <h2 className="font-serif text-lg text-[#0E2240] mb-5">
              {mode === "login" ? "Sign in" : mode === "forgot" ? "Forgot password" : "Reset password"}
            </h2>
            <form onSubmit={submit} className="space-y-4">
              {mode !== "reset" && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">Email</label>
                  <input data-testid="login-email-input" className={inputCls} type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@incheonkia.com" required />
                </div>
              )}
              {mode === "login" && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">Password</label>
                  <input data-testid="login-password-input" className={inputCls} type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} required />
                </div>
              )}
              {mode === "reset" && (
                <>
                  {issuedToken && (
                    <div data-testid="reset-token-display" className="text-xs bg-[#BF9B30]/10 border border-[#BF9B30]/40 rounded-sm p-3 break-all">
                      <span className="font-semibold text-[#0E2240]">Reset token (email delivery is mocked):</span>
                      <div className="mt-1 text-[#6B7280]">{issuedToken}</div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">Reset token</label>
                    <input data-testid="reset-token-input" className={inputCls} value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">New password</label>
                    <input data-testid="reset-new-password-input" className={inputCls} type="password" value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} minLength={8} required />
                  </div>
                </>
              )}
              {error && (
                <div data-testid="login-error" className="text-xs text-[#BB162B] border border-[#BB162B]/30 bg-[#BB162B]/5 rounded-sm p-2.5">
                  {error}
                </div>
              )}
              <button
                data-testid="login-submit-button"
                disabled={busy}
                className="w-full bg-[#0E2240] text-white text-sm py-2.5 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50"
              >
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "forgot" ? "Get reset token" : "Reset password"}
              </button>
            </form>
            <div className="mt-4 flex justify-between text-xs">
              {mode !== "login" ? (
                <button data-testid="back-to-login-link" onClick={() => { setMode("login"); setError(""); }} className="text-[#1E5AA8] hover:underline">
                  Back to sign in
                </button>
              ) : (
                <button data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }} className="text-[#1E5AA8] hover:underline">
                  Forgot password?
                </button>
              )}
            </div>
          </div>
          <p className="text-[11px] text-[#6B7280] mt-4">
            Google Workspace SSO — coming soon. Contact System Admin for access.
          </p>
        </div>
      </div>
    </div>
  );
}
